import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { api } from '../App';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Download, Lock, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// CSS for PDF viewer
const pdfViewerStyles = `
  .pdf-document {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .pdf-page-wrapper {
    margin-bottom: 16px;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
    border-radius: 4px;
    overflow: hidden;
  }
  
  .pdf-page-wrapper canvas {
    display: block;
    max-width: 100%;
    height: auto !important;
  }
  
  .pdf-controls {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border-radius: 8px;
    padding: 8px 12px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  .dark .pdf-controls {
    background: rgba(30, 41, 59, 0.95);
  }
  
  .pdf-scroll-container {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    padding: 8px;
  }
  
  .pdf-scroll-container::-webkit-scrollbar {
    width: 10px;
  }
  
  .pdf-scroll-container::-webkit-scrollbar-track {
    background: #e2e8f0;
    border-radius: 5px;
  }
  
  .pdf-scroll-container::-webkit-scrollbar-thumb {
    background: #10b981;
    border-radius: 5px;
  }
  
  @media (max-width: 768px) {
    .pdf-controls {
      padding: 6px 8px;
      gap: 4px;
    }
    
    .pdf-scroll-container::-webkit-scrollbar {
      width: 8px;
    }
  }
`;

export default function ParallelProvisionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    loadPdf();
  }, []);

  // Handle container resize for responsive PDF
  useEffect(() => {
    const updateWidth = () => {
      const container = document.getElementById('pdf-container');
      if (container) {
        setContainerWidth(container.offsetWidth - 32); // Account for padding
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [loading]);

  const loadPdf = async () => {
    try {
      const response = await api.get('/resources/parallel-provisions?action=view', {
        responseType: 'blob'
      });
      // Create a blob URL for react-pdf
      const url = URL.createObjectURL(response.data);
      setPdfData(url);
    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('Failed to load the document');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!user || !['active', 'lifetime'].includes(user.subscription_status)) {
      toast.error('Download is available for registered subscribers only. Please subscribe to download this resource.');
      return;
    }

    setDownloading(true);
    try {
      const response = await api.get('/resources/parallel-provisions?action=download', {
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'RA12009_RA9184_Parallel_Provisions.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      if (error.response?.status === 403) {
        toast.error('Download is available for registered subscribers only.');
      } else {
        toast.error('Failed to download the document');
      }
    } finally {
      setDownloading(false);
    }
  };

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
  }, []);

  const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, numPages || 1));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));

  const canDownload = user && ['active', 'lifetime'].includes(user.subscription_status);

  // Calculate page width based on container and scale
  const pageWidth = containerWidth > 0 ? Math.min(containerWidth * scale, containerWidth) : undefined;

  return (
    <>
      <style>{pdfViewerStyles}</style>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/library')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
              Parallel Provisions
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-4">Side-by-side comparison: RA 9184 vs RA 12009</p>
          </div>

          {/* Description Card */}
          <Card className="mb-4 bg-white dark:bg-slate-800 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg dark:text-slate-100">About This Resource</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Side-by-side comparison of parallel provisions under RA 12009 and RA 9184, 
                highlighting similarities, refinements, and provisions that are newly introduced or 
                repealed in the new law.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={handleDownload}
                  disabled={downloading}
                  className={canDownload 
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
                    : "bg-slate-400 text-white cursor-not-allowed"
                  }
                >
                  {downloading ? (
                    <>Downloading...</>
                  ) : canDownload ? (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Subscribe to Download
                    </>
                  )}
                </Button>
                
                {!canDownload && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 self-center">
                    Download is available for registered subscribers only
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* PDF Viewer */}
          <Card className="bg-white dark:bg-slate-800 dark:border-slate-700 overflow-hidden">
            <CardContent className="p-2 sm:p-4" id="pdf-container">
              {loading ? (
                <div className="flex items-center justify-center h-96">
                  <div className="text-slate-500 dark:text-slate-400">Loading document...</div>
                </div>
              ) : pdfData ? (
                <>
                  {/* PDF Controls */}
                  <div className="pdf-controls">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPrevPage}
                      disabled={currentPage <= 1}
                      className="h-8 px-2"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[80px] text-center">
                      {currentPage} / {numPages || '...'}
                    </span>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNextPage}
                      disabled={currentPage >= (numPages || 1)}
                      className="h-8 px-2"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    
                    <div className="h-6 w-px bg-slate-300 dark:bg-slate-600 mx-1 hidden sm:block" />
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={zoomOut}
                      disabled={scale <= 0.5}
                      className="h-8 px-2"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    
                    <span className="text-xs font-medium text-slate-600 dark:text-slate-400 min-w-[45px] text-center">
                      {Math.round(scale * 100)}%
                    </span>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={zoomIn}
                      disabled={scale >= 2.5}
                      className="h-8 px-2"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Scrollable PDF Container */}
                  <div 
                    className="pdf-scroll-container"
                    style={{ 
                      height: 'calc(100vh - 450px)',
                      minHeight: '400px'
                    }}
                  >
                    <Document
                      file={pdfData}
                      onLoadSuccess={onDocumentLoadSuccess}
                      loading={
                        <div className="flex items-center justify-center h-64">
                          <div className="text-slate-500">Loading PDF...</div>
                        </div>
                      }
                      error={
                        <div className="flex items-center justify-center h-64">
                          <div className="text-red-500">Failed to load PDF</div>
                        </div>
                      }
                      className="pdf-document"
                    >
                      <div className="pdf-page-wrapper">
                        <Page 
                          pageNumber={currentPage}
                          width={pageWidth}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                        />
                      </div>
                    </Document>
                  </div>
                  
                  {/* Mobile hint */}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 text-center">
                    Use the controls above to navigate pages and zoom. Swipe to scroll.
                  </p>
                </>
              ) : (
                <div className="flex items-center justify-center h-96">
                  <div className="text-slate-500 dark:text-slate-400">Failed to load document</div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <BottomNav />
      </div>
    </>
  );
}
